import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Icon } from './Icon.tsx'

/** Posun mysi, od ktereho je to tazeni a ne kliknuti na cip. */
const DRAG_PX = 5

/**
 * Vodorovna rada cipu bez posuvniku. Mysi se posouva sipkami (jen kdyz je kam)
 * a tazenim — kategorie v Akcich na pocitaci jinak nesly projit (Richard
 * 2026-10-13, posuvniky nechce). Na dotyku zustava prstem, sipky se skryji.
 */
export function ScrollRow({
  className,
  children,
  role,
  ariaLabel,
}: {
  /** Trida posouvaneho radku (chips-scroll, offers-bar-cats) — nese odsazeni a overflow. */
  className: string
  children: ReactNode
  role?: string | undefined
  ariaLabel?: string | undefined
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  function update() {
    const el = ref.current
    if (!el) return
    const left = el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }

  // Obsah se meni (pocty v cipech, jine kategorie) — prepocitat po kazdem vykresleni.
  useLayoutEffect(update)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [])

  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el || e.pointerType !== 'mouse' || e.button !== 0) return
    const startX = e.clientX
    const startScroll = el.scrollLeft
    let dragged = false
    // Posluchace na window: pointer capture se ztrati, kdyz React prekresli cip (springDrag past).
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      if (!dragged && Math.abs(dx) < DRAG_PX) return
      if (!dragged) el.classList.add('is-dragging')
      dragged = true
      el.scrollLeft = startScroll - dx
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      el.classList.remove('is-dragging')
      if (!dragged) return
      // Pusteni po tazeni neni kliknuti na cip pod mysi.
      const swallow = (ev: MouseEvent) => {
        ev.stopPropagation()
        ev.preventDefault()
      }
      el.addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  function scrollBy(direction: 1 | -1) {
    const el = ref.current
    if (el) el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.7), behavior: 'smooth' })
  }

  return (
    <div className="scroll-row">
      <div ref={ref} className={className} role={role} aria-label={ariaLabel} onPointerDown={startDrag}>
        {children}
      </div>
      {edges.left && (
        <button type="button" className="scroll-row-arrow is-left" aria-label="Posunout doleva" onClick={() => scrollBy(-1)}>
          <Icon name="chevron_left" size={18} />
        </button>
      )}
      {edges.right && (
        <button type="button" className="scroll-row-arrow is-right" aria-label="Posunout doprava" onClick={() => scrollBy(1)}>
          <Icon name="chevron_right" size={18} />
        </button>
      )}
    </div>
  )
}
